import { FRAME_LIKE_TYPES, ProductStatus, type ProductDetail } from '@optiflow/types';
import type { FrameRecommendationHandoff } from '@/modules/sales/recommendationHandoff';
import { readSelectionClaims } from '@/modules/sales/recommendationProvenance';
import { frameChoice, type SelectedFrame } from './draft';

/** How a recommendation hand-off ended on arrival; each has its own sentence on the screen. */
export type HandoffOutcome = 'seeded' | 'otherCustomer' | 'invalid' | 'unavailable';

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * Whether a hand-off may be taken by the builder of this customer, before anything is fetched for it.
 *
 * The token's own claims must name the hand-off's customer and frame, and that customer must be the builder's: a recommendation is never moved to another customer. Reading the claims is not verifying them — checkout does that.
 */
export function handoffRefusal(
  handoff: FrameRecommendationHandoff,
  customerId: string,
): 'otherCustomer' | 'invalid' | null {
  const claims = readSelectionClaims(handoff.selectionToken);
  if (
    !claims ||
    !same(claims.customerId, handoff.customerId) ||
    !same(claims.variantId, handoff.variantId)
  ) {
    return 'invalid';
  }
  return same(handoff.customerId, customerId) ? null : 'otherCustomer';
}

/**
 * The recommended frame as the builder holds a chosen one, read from the catalogue as it stands now.
 *
 * Null when the product is no longer sold, the variant is gone or retired, or it is not a frame. It carries no price from the recommendation: the server prices it like any other frame.
 */
export function recommendedFrame(
  handoff: FrameRecommendationHandoff,
  product: ProductDetail,
): SelectedFrame | null {
  if (!FRAME_LIKE_TYPES.includes(product.type) || product.status !== ProductStatus.ACTIVE) {
    return null;
  }
  const variant = product.variants.find((entry) => same(entry.id, handoff.variantId));
  if (!variant || !variant.isActive) return null;
  return frameChoice(variant, product, null, handoff.selectionToken);
}

/**
 * The pairs whose recommendation checkout refused, from the fields it names.
 *
 * The server names a refused line `lines.N.recommendationToken` by its place in the request, and the builder always sends each pair's frame, right lens and left lens in that order, so line N is pair N/3. A refusal that names no line of a recommended pair is taken to concern every recommended pair: none is stripped without the user.
 */
export function refusedRecommendationPairs(
  fields: readonly (string | undefined)[],
  pairs: readonly { id: string; frame: SelectedFrame | null }[],
): string[] {
  const recommended = pairs.filter((pair) => pair.frame?.recommendationToken);
  const named = fields.flatMap((field) => {
    const match = /^lines(?:\.(\d+)|\[(\d+)\])\.recommendationToken$/.exec(field ?? '');
    const index = match ? Number(match[1] ?? match[2]) : Number.NaN;
    const pair = Number.isInteger(index) ? pairs[Math.floor(index / 3)] : undefined;
    return pair && index % 3 === 0 && pair.frame?.recommendationToken ? [pair.id] : [];
  });
  return named.length > 0 ? [...new Set(named)] : recommended.map((pair) => pair.id);
}
